import { createElement, useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { ArrowLeft, Mic, MicOff, MessageCircle, MonitorSmartphone, Loader2 } from "lucide-react";
import LienDemoVoix from "@/components/LienDemoVoix";
import OrbeGrafton from "@/components/OrbeGrafton";

const API_URL = process.env.REACT_APP_BACKEND_URL || "";
const WIDGET_SRC = "https://unpkg.com/@elevenlabs/convai-widget-embed";
const HEARTBEAT_MS = 30000;

const SUGGESTIONS = [
  "Présentez-vous en quelques mots.",
  "Où en est l'activité aujourd'hui ?",
  "Pourquoi le développement du produit est-il en pause ?",
  "Quel est le chiffre d'affaires réalisé cette année ?",
  "Quelles aides de financement restent ouvertes ?",
  "Que font les agents NanoCorp ?",
];

type Status = "idle" | "starting" | "active" | "locked" | "error";

function getDeviceId(): string {
  try {
    let id = localStorage.getItem("grafton_device_id");
    if (!id) {
      id = crypto.randomUUID();
      localStorage.setItem("grafton_device_id", id);
    }
    return id;
  } catch {
    return `anon-${Math.random().toString(36).slice(2)}`;
  }
}

export default function VoixGrafton() {
  const navigate = useNavigate();
  const [status, setStatus] = useState<Status>("idle");
  const [signedUrl, setSignedUrl] = useState("");
  const [userName, setUserName] = useState("Joël");
  const deviceId = useRef(getDeviceId());
  const timer = useRef<number | null>(null);
  const activeRef = useRef(false);

  const post = useCallback(
    (path: string, body: Record<string, unknown>) =>
      fetch(`${API_URL}/api/voice/${path}`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ device_id: deviceId.current, ...body }),
      }),
    []
  );

  const stopHeartbeat = () => {
    if (timer.current) window.clearInterval(timer.current);
    timer.current = null;
  };

  const start = useCallback(
    async (takeover = false) => {
      setStatus("starting");
      try {
        if (!document.querySelector(`script[src="${WIDGET_SRC}"]`)) {
          const s = document.createElement("script");
          s.src = WIDGET_SRC;
          s.async = true;
          document.body.appendChild(s);
        }
        const res = await post("session", { takeover });
        if (res.status === 409) {
          setStatus("locked");
          return;
        }
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const data = await res.json();
        setSignedUrl(data.signed_url);
        setUserName(data.user_name || "Joël");
        activeRef.current = true;
        setStatus("active");
        stopHeartbeat();
        timer.current = window.setInterval(async () => {
          try {
            const hb = await post("heartbeat", {});
            const j = await hb.json();
            if (!j.active) {
              activeRef.current = false;
              stopHeartbeat();
              setSignedUrl("");
              setStatus("locked");
            }
          } catch {
            /* réseau instable : on réessaie au prochain battement */
          }
        }, HEARTBEAT_MS);
      } catch {
        setStatus("error");
      }
    },
    [post]
  );

  const stop = useCallback(async () => {
    stopHeartbeat();
    activeRef.current = false;
    setSignedUrl("");
    setStatus("idle");
    try {
      await post("release", {});
    } catch {
      /* le verrou expire seul après 90 s */
    }
  }, [post]);

  useEffect(() => {
    const release = () => {
      if (!activeRef.current) return;
      const blob = new Blob([JSON.stringify({ device_id: deviceId.current })], { type: "application/json" });
      navigator.sendBeacon(`${API_URL}/api/voice/release`, blob);
    };
    window.addEventListener("pagehide", release);
    return () => {
      window.removeEventListener("pagehide", release);
      release();
      stopHeartbeat();
    };
  }, []);

  return (
    <div className="min-h-screen pb-24">
      <div className="sticky top-0 z-10 flex items-center gap-3 border-b border-border/50 bg-background/95 px-4 py-3 backdrop-blur">
        <Button variant="ghost" size="icon" onClick={() => navigate(-1)}>
          <ArrowLeft className="h-5 w-5" />
        </Button>
        <OrbeGrafton taille={40} pose="neutre" />
        <div>
          <h1 className="text-lg font-bold leading-tight">Voix de Grafton</h1>
          <p className="text-xs text-muted-foreground">Parlez à Grafton IA, il vous répond à voix haute</p>
        </div>
      </div>

      <div className="mx-auto max-w-2xl space-y-4 p-4">
        <div className="flex justify-center pt-2" data-testid="orbe-grafton">
          <OrbeGrafton taille={170} pose={status === "active" ? "ecoute" : "neutre"} pensant={status === "starting"} />
        </div>
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-base">Conversation vocale</CardTitle>
            <CardDescription>
              Grafton IA est une intelligence artificielle : il se présente comme telle et ne conserve aucun enregistrement. Une seule session vocale peut être active à la fois.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="flex min-h-[200px] flex-col items-center justify-center gap-3 rounded-lg border border-dashed border-border/60 p-4 text-center">
              {status === "idle" && (
                <Button onClick={() => start(false)} className="gap-2" data-testid="voice-start">
                  <Mic className="h-4 w-4" /> Activer la voix
                </Button>
              )}
              {status === "starting" && (
                <p className="flex items-center gap-2 text-sm text-muted-foreground">
                  <Loader2 className="h-4 w-4 animate-spin" /> Connexion sécurisée…
                </p>
              )}
              {status === "active" && signedUrl && (
                <>
                  {createElement("elevenlabs-convai", {
                    key: signedUrl,
                    "signed-url": signedUrl,
                    "dynamic-variables": JSON.stringify({ user_name: userName }),
                  })}
                  <Button variant="ghost" size="sm" onClick={stop} className="gap-1.5 text-xs text-muted-foreground">
                    <MicOff className="h-3.5 w-3.5" /> Arrêter la voix
                  </Button>
                </>
              )}
              {status === "locked" && (
                <>
                  <MonitorSmartphone className="h-8 w-8 text-amber-500" />
                  <p className="text-sm">
                    Une session vocale est déjà active sur un autre appareil (téléphone ou ordinateur).
                  </p>
                  <Button onClick={() => start(true)} variant="outline" className="gap-2" data-testid="voice-takeover">
                    <Mic className="h-4 w-4" /> Prendre la main ici
                  </Button>
                </>
              )}
              {status === "error" && (
                <>
                  <p className="text-sm text-destructive">La voix est momentanément indisponible.</p>
                  <Button onClick={() => start(false)} variant="outline" size="sm">Réessayer</Button>
                </>
              )}
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <div className="flex items-center gap-2">
              <MessageCircle className="h-4 w-4 text-primary" />
              <CardTitle className="text-base">Questions à poser</CardTitle>
            </div>
          </CardHeader>
          <CardContent>
            <ul className="space-y-1.5 text-sm text-muted-foreground">
              {SUGGESTIONS.map((q) => (
                <li key={q} className="rounded-md bg-muted/40 px-3 py-2">« {q} »</li>
              ))}
            </ul>
          </CardContent>
        </Card>

        <LienDemoVoix />
      </div>
    </div>
  );
}
